import type {
  CityDto,
  CreateOrderResponse,
  CursorPage,
  DisputeOutcome,
  OrderCategory,
  OrderDto,
  OrderScope,
  PriceSuggestionDto,
  SizeClass,
} from '@posilka/shared';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from './client';

export const orderKeys = {
  all: ['orders'] as const,
  mine: (scope: OrderScope) => ['orders', 'my', scope] as const,
  detail: (id: string) => ['orders', 'detail', id] as const,
  code: (id: string) => ['orders', 'code', id] as const,
};

export const useCities = () =>
  useQuery({
    queryKey: ['cities'],
    queryFn: () => api<CityDto[]>('/cities'),
    staleTime: 60 * 60_000,
  });

export const usePriceSuggestion = (
  fromCityId: string | undefined,
  toCityId: string | undefined,
  sizeClass: SizeClass | undefined,
) =>
  useQuery({
    queryKey: ['pricing', fromCityId, toCityId, sizeClass],
    queryFn: () =>
      api<PriceSuggestionDto>(
        `/pricing/suggest?fromCityId=${fromCityId}&toCityId=${toCityId}&sizeClass=${sizeClass}`,
      ),
    enabled: !!fromCityId && !!toCityId && !!sizeClass && fromCityId !== toCityId,
    retry: false,
    placeholderData: keepPreviousData,
  });

export const useMyOrders = (scope: OrderScope) =>
  useInfiniteQuery({
    queryKey: orderKeys.mine(scope),
    queryFn: ({ pageParam }) =>
      api<CursorPage<OrderDto>>(
        `/orders/my?status=${scope}${pageParam ? `&cursor=${pageParam}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

export const useOrder = (id: string) =>
  useQuery({
    queryKey: orderKeys.detail(id),
    queryFn: () => api<OrderDto>(`/orders/${id}`),
    // Holat boshqa tomonda o'zgaradi (qabul, olish) — sahifa ochiqligida yangilab turamiz.
    refetchInterval: 20_000,
  });

export const useOrderCode = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: orderKeys.code(id),
    queryFn: () => api<{ code: string }>(`/orders/${id}/code`),
    enabled,
    staleTime: Infinity,
  });

/** Buyurtma o'zgargach — tafsilot keshini yangilab, ro'yxatlarni eskirgan deb belgilaydi. */
export const useOrderMutation = <TVars>(fn: (vars: TVars) => Promise<OrderDto>) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (order) => {
      qc.setQueryData(orderKeys.detail(order.id), order);
      void qc.invalidateQueries({ queryKey: orderKeys.all, refetchType: 'none' });
      void qc.invalidateQueries({ queryKey: ['driver'] });
    },
  });
};

export const useCreateOrder = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => api<CreateOrderResponse>('/orders', { method: 'POST', body }),
    onSuccess: (res) => {
      qc.setQueryData(orderKeys.detail(res.order.id), res.order);
      qc.setQueryData(orderKeys.code(res.order.id), { code: res.deliveryCode });
      void qc.invalidateQueries({ queryKey: orderKeys.all });
    },
  });
};

export const cancelOrder = (id: string, reason?: string) =>
  api<OrderDto>(`/orders/${id}/cancel`, { method: 'POST', body: { reason } });

export const raiseOrderPrice = (id: string, price: number) =>
  api<OrderDto>(`/orders/${id}/price`, { method: 'PATCH', body: { price } });

export const resolveDispute = (id: string, outcome: DisputeOutcome, note: string) =>
  api<OrderDto>(`/admin/orders/${id}/resolve-dispute`, { method: 'POST', body: { outcome, note } });

/** Qimmatbaho bo'lishi mumkin — yo'qolish xavfi haqida ogohlantirish ko'rsatiladi. */
export const isValuable = (category: OrderCategory | undefined, size: SizeClass | undefined) =>
  size === 'XL' || category === 'ELECTRONICS' || category === 'PHONE';
